import React, { useEffect, useState, useCallback } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import Logo from './Logo';
import { smoothScrollToId } from '../lib/smoothScroll';

const ANCHOR_ITEMS = [
  { id: 'how-it-works', label: 'How it works' },
  { id: 'pricing', label: 'Pricing' },
];

export default function Navbar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isHome = pathname === '/';

  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Track whether user has scrolled past the hero — used for the border.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Active section highlight (homepage only). Never writes to the URL.
  useEffect(() => {
    if (!isHome) {
      setActive(null);
      return undefined;
    }
    const els = ANCHOR_ITEMS.map((i) => document.getElementById(i.id)).filter(Boolean);
    if (els.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the top-most section currently intersecting the trigger band.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActive(visible[0].target.id);
        else setActive(null);
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [isHome, pathname]);

  // Lock body scroll while the mobile menu is open.
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [mobileOpen]);

  const handleAnchor = useCallback(
    (id) => (e) => {
      e.preventDefault();
      setMobileOpen(false);
      if (isHome) {
        smoothScrollToId(id);
        // Update URL on click (not on scroll). Use replaceState to keep
        // browser history clean.
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, '', `#${id}`);
        }
      } else {
        // Navigate home with a hash; ScrollManager handles the smooth scroll.
        navigate(`/#${id}`);
      }
    },
    [isHome, navigate]
  );

  const anchorClass = (id) => {
    const isActive = isHome && active === id;
    return `relative transition-colors duration-300 ease-out focus:outline-none focus:ring-2 focus:ring-[#6B4BEF] rounded px-1 ${
      isActive ? 'text-[#5132d4] font-semibold' : 'text-[#3b3656] hover:text-[#5132d4]'
    }`;
  };

  const AnchorUnderline = ({ show }) => (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute left-0 right-0 -bottom-1 h-[2px] rounded-full transition-opacity duration-300 ease-out"
      style={{ backgroundColor: '#5132d4', opacity: show ? 1 : 0 }}
    />
  );

  return (
    <>
      <nav
        data-testid="site-nav"
        className={`sticky top-0 z-30 backdrop-blur-md bg-[#FAFAF9]/85 transition-[border-color,box-shadow] duration-300 ease-out ${
          scrolled ? 'border-b border-[#e8e4da] shadow-[0_2px_10px_-6px_rgba(19,15,40,0.15)]' : 'border-b border-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center gap-3">
          {isHome ? (
            <a
              href="#top"
              onClick={(e) => {
                e.preventDefault();
                setMobileOpen(false);
                smoothScrollToId('top');
                if (window.history && window.history.replaceState) {
                  window.history.replaceState(null, '', window.location.pathname);
                }
              }}
              className="flex items-center gap-2 sm:gap-2.5 min-w-0"
            >
              <Logo size={32} />
              <span className="font-serif text-lg sm:text-xl lg:text-2xl tracking-tight truncate" style={{ color: '#130F28' }}>
                Crush Marketing
              </span>
            </a>
          ) : (
            <Link to="/" className="flex items-center gap-2 sm:gap-2.5 min-w-0">
              <Logo size={32} />
              <span className="font-serif text-lg sm:text-xl lg:text-2xl tracking-tight truncate" style={{ color: '#130F28' }}>
                Crush Marketing
              </span>
            </Link>
          )}

          {/* Desktop nav */}
          <div className="ml-auto hidden md:flex items-center gap-6 lg:gap-8 text-sm sm:text-[15px] font-medium">
            {ANCHOR_ITEMS.map((item) => (
              <a
                key={item.id}
                href={isHome ? `#${item.id}` : `/#${item.id}`}
                data-testid={`nav-${item.id}`}
                onClick={handleAnchor(item.id)}
                className={anchorClass(item.id)}
              >
                {item.label}
                <AnchorUnderline show={isHome && active === item.id} />
              </a>
            ))}
            <NavLink
              to="/about"
              data-testid="nav-about"
              className={({ isActive }) =>
                `relative transition-colors duration-300 ease-out focus:outline-none focus:ring-2 focus:ring-[#6B4BEF] rounded px-1 ${
                  isActive ? 'text-[#5132d4] font-semibold' : 'text-[#3b3656] hover:text-[#5132d4]'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  About
                  <AnchorUnderline show={isActive} />
                </>
              )}
            </NavLink>
          </div>

          {/* Mobile toggle */}
          <button
            type="button"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav-panel"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            className="ml-auto md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full focus:outline-none focus:ring-2 focus:ring-[#6B4BEF] transition-colors"
            style={{ color: '#3b3656' }}
          >
            {mobileOpen ? <X size={22} strokeWidth={2.25} /> : <Menu size={22} strokeWidth={2.25} />}
          </button>
        </div>

        {/* Mobile panel */}
        <div
          id="mobile-nav-panel"
          data-testid="nav-mobile-panel"
          className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-300 ease-out border-t ${
            mobileOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
          style={{ borderColor: mobileOpen ? '#e8e4da' : 'transparent', backgroundColor: '#FAFAF9' }}
          aria-hidden={!mobileOpen}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col text-base font-medium">
            {ANCHOR_ITEMS.map((item) => (
              <a
                key={item.id}
                href={isHome ? `#${item.id}` : `/#${item.id}`}
                onClick={handleAnchor(item.id)}
                data-testid={`nav-mobile-${item.id}`}
                className={`py-3 border-b transition-colors ${
                  isHome && active === item.id ? 'text-[#5132d4] font-semibold' : 'text-[#1f1c33]'
                }`}
                style={{ borderColor: '#e8e4da' }}
              >
                {item.label}
              </a>
            ))}
            <NavLink
              to="/about"
              data-testid="nav-mobile-about"
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `py-3 transition-colors ${isActive ? 'text-[#5132d4] font-semibold' : 'text-[#1f1c33]'}`
              }
            >
              About
            </NavLink>
          </div>
        </div>
      </nav>
    </>
  );
}
